/**
 * WebRTC statistics projection and sampler for the Session Flight Recorder (AVS-4430, parent
 * AVS-4423).
 *
 * ## Why this module exists
 *
 * `grep -rn getStats src/` returned exactly one hit before this file — a comment in a test. The app
 * had no access to packet loss, jitter, `bytesReceived`, `framesDecoded`, RTT or the selected
 * candidate pair, and its entire render predicate was "does a stream object exist". Two questions
 * the 2026-09-04 multi-party incident had to settle by inference are answered here by a number:
 *
 * - `inbound-rtp.framesDecoded` frozen at **0** means negotiated but never carried RTP; frozen at
 *   **N > 0** means carried then died. That single value separates a dropped signaling message from
 *   a dead peer.
 * - `candidate-pair(selected).remoteCandidateType` — `relay` vs `host`/`srflx` — is literally "did
 *   the corporate VPN force TURN?".
 *
 * ## A rewrite of `tests/dcc-integration/smoke/voipProbes.ts`, not a move
 *
 * The measured baselines and the projection shape come from `summarizeInboundVideoStats` (:307),
 * `summarizeOutboundVideoStats` (:330) and `readVideoStatsEntries` (:432) there, which were built
 * against a real dev call. But that code is `async function(page: Page)` — Playwright
 * `page.evaluate` source with test-oracle degradation semantics — and `src/` cannot import from
 * `tests/` anyway (tsconfig excludes it). What is ported is the projection logic and the measured
 * invariants; what is left behind is the harness.
 *
 * **`framesReceived` and `framesDecoded` are summed independently and never compared.** A healthy
 * call measured 730 vs 727, so any equality assertion between them fails live calls.
 *
 * ## Performance budget
 *
 * This runs on a machine already encoding six video streams, and `getStats()` is a per-peer async
 * call whose N is the thing that already hurts. Every read is behind the caller's `shouldSample()`
 * gate (the tap passes `isRecording() && !isLaneShed('rtcstats')`), the cadence obeys the adaptive
 * throttle's `samplingMultiplier`, and the sample is **columnar** — parallel arrays keyed by peer
 * index rather than one object per peer per stat.
 *
 * ## Privacy tiering lives in the core redactor, not here
 *
 * SDP and ICE candidates carry IP addresses — personal data, and also exactly the evidence RC10
 * needed. This module used to apply the AVS-4423 tier itself, before `record()`; `recorder/redact.ts`
 * then applied its own on top and hashed the hash. The tier is implemented once now, there, on the
 * path every `record()` call already takes.
 *
 * What stays here is PROJECTION, and the projection is what makes the tier reachable at all. A raw
 * `RTCIceCandidate` is a class instance the redactor tags rather than walks, and a raw stats report
 * is a `ReadonlyMap`; flattening both into plain objects is what puts `address`, `relatedAddress`
 * and the `candidate:` line where the redactor can see them. Two shapes here are load-bearing for
 * that tier and are documented where they are built:
 *
 * - `relatedAddress` is emitted under that exact key, because `raddr` on a RELAY candidate is the
 *   CLIENT's own mapped address, and `recorder/redact.ts` always hashes that key — tiering it by
 *   the candidate's type would keep a real person's public IP verbatim on exactly the candidates a
 *   VPN'd corporate network produces.
 * - {@link IRtcStatsSample} keeps `remoteCandidateAddress` as a column parallel to
 *   `remoteCandidateType`, which is how the redactor knows which entries are our own TURN server's.
 */

/**
 * Healthy sampling cadence. The spec's window is 2-5 s; 3 s matches the frame-delta spacing the
 * dcc-integration oracle settled on, which is long enough for a 30 fps stream to move ~90 frames
 * and short enough to catch a stall inside one screenshot's worth of time.
 *
 * Not `@deprecated` (final review): the sampler's base cadence is INJECTED now
 * (`IRtcStatsSamplerOptions.baseIntervalMs`) rather than read from this module constant as the
 * primary value — `full` (dev/prelive) samples at 1 s, not 3 — but {@link rtcStatsIntervalMs} reads
 * this constant as its OWN degenerate-value floor fallback, so it is a live runtime value, not a
 * leftover kept only for outside importers. Must not be removed.
 */
export const RTC_STATS_BASE_INTERVAL_MS = 3_000;

/**
 * Consecutive peerless ticks after which the loop parks itself.
 *
 * A parked sampler costs nothing and is re-armed by the next peer-connection event, which is also
 * what carries it across a VOIP takeover: `destroy()` nulls the singleton and the rebuilt client
 * constructs fresh peer connections, each of which re-arms.
 */
export const RTC_STATS_IDLE_TICKS_BEFORE_PARK = 3;

/** One peer, as the sampler needs to see it. Read live per tick — never cached by this module. */
export interface IRtcStatsPeer {
  connectionId: string;
  peerConnection: RTCPeerConnection;
  /** `stream == null` is the GRAY tile: the strong transmission-failure signal, and it is free. */
  hasStream: boolean;
  /** The recovery ladder wrote this transport off (`webrtc/index.ts` `markTransportFailed`). */
  transportFailed: boolean;
}

/** The selected candidate pair. Addresses are RAW; `recorder/redact.ts` tiers them. */
export interface ISelectedPair {
  /** `host` | `srflx` | `prflx` | `relay` | `''` when no pair is selected yet. */
  localType: string;
  remoteType: string;
  /** `udp` | `tcp` | `''`. */
  protocol: string;
  /** `''`, or the TURN transport for a relay pair. */
  relayProtocol: string;
  /**
   * RAW peer addresses, `''` when unknown. Named `remoteAddress`/`localAddress` beside
   * `remoteType`/`localType` so `recorder/redact.ts` can tier each against its own candidate type.
   */
  remoteAddress: string;
  localAddress: string;
  /** The TURN server URL for a relay pair — raw, and never carrying a credential. */
  url: string;
  /** `currentRoundTripTime`, in ms. `-1` when the pair reports none. */
  roundTripMs: number;
  /**
   * Present only on the selected pair, so absent before the first peer connects and on recvonly
   * peers, and unimplemented in Firefox. Recorded as a bonus, NEVER relied on as a signal.
   */
  availableOutgoingBitrate: number;
}

/** Inbound video counters, summed across every inbound video entry of one report. */
export interface IInboundVideo {
  framesDecoded: number;
  framesReceived: number;
  packetsReceived: number;
  packetsLost: number;
  /** `jitter` is reported in seconds; this is milliseconds. */
  jitterMs: number;
  /** How many inbound VIDEO entries were summed. `0` means "this report had no inbound video". */
  trackCount: number;
}

/** Outbound video counters — the twin of {@link IInboundVideo}. */
export interface IOutboundVideo {
  framesSent: number;
  framesEncoded: number;
  packetsSent: number;
  trackCount: number;
  /**
   * `'none' | 'cpu' | 'bandwidth' | 'other' | ''` — WHY the encoder throttled, when it did. The
   * first non-empty value seen across this report's outbound video entries wins: unlike the packet
   * counters above, summing a reason string has no meaning.
   */
  qualityLimitationReason: string;
}

/**
 * Inbound AUDIO counters, summed across every inbound audio entry of one report (AVS-4423 Task
 * 1.7) — the audio twin of {@link IInboundVideo}. Before this existed the stats sample had no audio
 * columns at all, so a recording could never answer "was audio arriving?", the single most
 * important question in a call where someone cannot be heard.
 *
 * `audioLevel` and `totalAudioEnergy` are per-track instantaneous/cumulative measures, not packet
 * counters, but this sums them the same way as every other field here for one projection to stay
 * uniform — a peer with more than one inbound audio track has not been a shape this module has seen.
 */
export interface IInboundAudio {
  packetsReceived: number;
  packetsLost: number;
  bytesReceived: number;
  audioLevel: number;
  totalAudioEnergy: number;
  trackCount: number;
}

/** One peer's projection of one `getStats()` report. */
export interface IPeerStats {
  inbound: IInboundVideo;
  outbound: IOutboundVideo;
  audio: IInboundAudio;
  pair: ISelectedPair;
}

/**
 * One sampler tick across every peer, **columnar**: `peers[i]` names the peer every other array is
 * indexed by. A per-peer object per stat would multiply the key names by N on a machine that is
 * already the bottleneck.
 */
export interface IRtcStatsSample {
  peers: string[];
  framesDecoded: number[];
  framesReceived: number[];
  framesSent: number[];
  framesEncoded: number[];
  packetsReceived: number[];
  packetsLost: number[];
  packetsSent: number[];
  jitterMs: number[];
  roundTripMs: number[];
  localCandidateType: string[];
  remoteCandidateType: string[];
  candidateProtocol: string[];
  relayProtocol: string[];
  /** RAW, and parallel to `remoteCandidateType`, which `recorder/redact.ts` tiers it against. */
  remoteCandidateAddress: string[];
  turnUrl: string[];
  availableOutgoingBitrate: number[];
  inboundVideoTracks: number[];
  outboundVideoTracks: number[];
  hasStream: boolean[];
  transportFailed: boolean[];
  connectionState: string[];
  iceConnectionState: string[];
  /** Peers whose `getStats()` rejected — a read that failed is not a peer that is silent. */
  failed: string[];
  /**
   * AVS-4423 Task 1.7 — audio columns, index-aligned with `peers` exactly like every column above.
   * From `inbound-rtp` entries whose `kind` is `'audio'`.
   */
  audioPacketsReceived: number[];
  audioPacketsLost: number[];
  audioBytesReceived: number[];
  audioLevel: number[];
  totalAudioEnergy: number[];
  /**
   * How many inbound AUDIO entries were summed into the five columns above — the audio twin of
   * `inboundVideoTracks`. Load-bearing: without this, a peer with a real, present, silent audio
   * entry (`audioPacketsReceived: 0`) is indistinguishable from a peer with NO inbound audio entry
   * at all — "sent nothing" and "never measured" collapse into the same all-zero row, which is
   * exactly the distinction a call where someone cannot be heard needs told apart.
   */
  audioTracks: number[];
  /**
   * From `outbound-rtp` VIDEO entries only (`summarizeOutboundVideo`'s `isOutboundVideo` filter) —
   * why the encoder throttled, per peer. Chrome reports `'none'`, NOT `''`, when nothing limited
   * encoding — read a `''` row as "unknown", never as "healthy". `''` means either there was no
   * outbound video entry to read at all (see `outboundVideoTracks` above, which is what actually
   * distinguishes that from a real zero-track peer) or the browser does not implement the field at
   * all (Firefox). Named for the scope it actually has: this says nothing about the audio path, and
   * sits beside five genuinely audio columns in the recorded JSON, so an unscoped name here would
   * read as "no quality problem" when audio is what failed.
   */
  videoQualityLimitationReason: string[];
}

/**
 * The derived media verdict for one peer.
 *
 * `everDecoded` is the discriminator the incident analysis lacked: `false` with a connected
 * transport means negotiated but never carried RTP; `true` with `decoding: false` means it carried
 * and then died.
 */
export interface IMediaHealthEntry {
  connectionId: string;
  hasStream: boolean;
  transportFailed: boolean;
  framesDecoded: number;
  framesDecodedDelta: number;
  decoding: boolean;
  everDecoded: boolean;
  connectionState: string;
  iceConnectionState: string;
  /** Which field changed since the last tick — why this entry is in the record at all. */
  changed: string[];
}

/** Options for {@link createRtcStatsSampler}. Everything is injected so the loop needs no globals. */
export interface IRtcStatsSamplerOptions {
  /** Resolved LIVE, per tick. Must re-read the singleton, never close over one. */
  readPeers: () => IRtcStatsPeer[];
  /** `isRecording() && !isLaneShed('rtcstats')`. Checked BEFORE any `getStats()` call. */
  shouldSample: () => boolean;
  /**
   * True once the recorder is off for good — the kill switch honoured, or the circuit breaker
   * tripped. Both are permanent for the tab by design, so the loop stops rather than holding a
   * timer for the rest of the call. A merely SHED lane is not terminated: the throttle recovers,
   * and parking on it would leave the sampler dark for the rest of a call that got better.
   */
  isTerminated: () => boolean;
  /** The adaptive throttle's `samplingMultiplier`. */
  samplingMultiplier: () => number;
  /**
   * The active profile's healthy cadence for this lane (AVS-4423 Task 1.3), e.g.
   * `recorderProfile().rtcStatsIntervalMs`. A FUNCTION, not a value, read fresh on every
   * `schedule()` call — so a profile change is picked up on the sampler's next tick without needing
   * to reconstruct it, exactly like {@link samplingMultiplier} already is.
   */
  baseIntervalMs: () => number;
  onSample: (sample: IRtcStatsSample) => void;
  /** Called only with the entries whose verdict CHANGED, so a steady call records nothing here. */
  onHealth: (entries: IMediaHealthEntry[]) => void;
}

/** The sampler's control surface. */
export interface IRtcStatsSampler {
  /** Idempotent. Starts the loop, or un-parks it after an idle stop. */
  arm(): void;
  /** Stops the loop and forgets the per-peer frame history. */
  stop(): void;
  /** True while a tick is scheduled. */
  isArmed(): boolean;
}

interface IPeerHistory {
  framesDecoded: number;
  everDecoded: boolean;
  hasStream: boolean;
  transportFailed: boolean;
  decoding: boolean;
  connectionState: string;
}

/**
 * One raw stats record.
 *
 * `RTCStatsReport` values cross an `any`-typed DOM boundary, so every field is re-read through the
 * `num`/`str` guards below rather than spread wholesale into a projection. Exported because it is
 * the input type of every projection function here.
 */
export type TRawStat = Record<string, unknown>;

/**
 * The interval one tick should wait, given the active profile's healthy cadence and the adaptive
 * throttle's multiplier.
 *
 * `baseIntervalMs` is INJECTED (AVS-4423 Task 1.3) rather than read from a module constant, because
 * `full` and `adaptive` do not share one legal window — `full`'s 1 s healthy cadence would have
 * been clamped up to the old fixed `[2 000, 5 000]` ms bound built for `adaptive`'s 3 s.
 *
 * Fix round 1: an earlier version of this floor was `Math.min(baseIntervalMs * 2,
 * Math.max(baseIntervalMs / 2, baseIntervalMs))`, which LOOKS like a clamp but is an arithmetic
 * identity — for any positive `b`, `max(b/2, b) === b` and `min(2b, b) === b`, so it always returns
 * its own input and guards nothing. A degenerate `baseIntervalMs` (`0`, negative, `NaN`, `Infinity`)
 * passed straight through to `setTimeout`, which treats each of those as ~0/1 ms — an unbounded
 * `getStats()` loop over every peer, on the machine already the bottleneck. Not live under either
 * shipped profile (`1 000`/`3 000`), but `profile.ts` already uses `0` as "off" for two sibling
 * fields (`streamFrameIntervalMs`, `domSnapshotIntervalMs`), so an `rtcStatsIntervalMs: 0` row is a
 * realistic future edit this file cannot see coming from its own contract alone. The real guard: a
 * non-finite or non-positive base falls back to the `adaptive` default; a finite positive one is
 * floored at 250 ms so no editor typo can schedule a hot loop either. The multiplier gets the
 * matching validation one line below (`Number.isFinite(multiplier) && multiplier >= 1`) — before
 * this fix the base had none at all.
 */
export function rtcStatsIntervalMs(baseIntervalMs: number, multiplier: number): number {
  const base =
    Number.isFinite(baseIntervalMs) && baseIntervalMs > 0 ? Math.max(250, baseIntervalMs) : RTC_STATS_BASE_INTERVAL_MS;
  const factor = Number.isFinite(multiplier) && multiplier >= 1 ? multiplier : 1;

  return Math.round(base * factor);
}

/**
 * Projects one ICE candidate — an `RTCIceCandidate` or the plain object that crosses the wire.
 *
 * Both shapes occur: `onicecandidate` hands out a class instance the recorder's redactor tags
 * rather than walks, and a remote candidate arrives from signaling as JSON. Flattening both into
 * one plain shape is what makes the candidate queryable — and what puts its addresses under the
 * key names `recorder/redact.ts` tiers them by.
 */
export function projectIceCandidate(candidate: unknown): Record<string, unknown> {
  if (typeof candidate !== 'object' || candidate === null) {
    return { present: false };
  }

  const source = candidate as TRawStat;
  const line = str(source, 'candidate');
  const parsed = parseCandidateLine(line);
  const type = str(source, 'type') || parsed.type;
  const address = str(source, 'address') || parsed.address;
  const related = str(source, 'relatedAddress') || parsed.relatedAddress;

  return {
    present: true,
    type,
    protocol: str(source, 'protocol') || parsed.protocol,
    // Raw, and named so the redactor can tier them: `address` against the `type` beside it, and
    // `relatedAddress` unconditionally, because on a relay candidate that is the CLIENT's address.
    address,
    port: num(source, 'port', parsed.port),
    relatedAddress: related,
    relatedPort: num(source, 'relatedPort', -1),
    sdpMid: str(source, 'sdpMid'),
    sdpMLineIndex: num(source, 'sdpMLineIndex', -1),
    // The URL of the STUN/TURN server that produced this candidate — our own infrastructure, and
    // the direct answer to "did TURN even get asked". Never carries a credential.
    url: str(source, 'url'),
    relayProtocol: str(source, 'relayProtocol'),
  };
}

/** Counts of each candidate type in an SDP — survives even when the redacted text gets truncated. */
export function summarizeSdpCandidates(sdp: string): Record<string, number> {
  const counts: Record<string, number> = { host: 0, srflx: 0, prflx: 0, relay: 0 };

  for (const raw of sdp.split('\n')) {
    const line = raw.trim();

    if (!line.startsWith('a=candidate:')) {
      continue;
    }

    const type = parseCandidateLine(line).type;

    if (type) {
      counts[type] = (counts[type] ?? 0) + 1;
    }
  }

  return counts;
}

/**
 * Projects a local or remote session description into a recordable shape.
 *
 * The candidate-type summary is carried ALONGSIDE the text on purpose: the recorder's redactor caps
 * a single string at 4 096 characters, and a multi-candidate SDP is routinely longer than that, so
 * the counts survive a truncation the text does not. The `sdp` itself goes out RAW — the redactor
 * recognises it and applies the tier on the way into the ring buffer.
 */
export function projectSessionDescription(
  description: { type?: string; sdp?: string } | null | undefined
): Record<string, unknown> {
  if (!description) {
    return { present: false };
  }

  const sdp = typeof description.sdp === 'string' ? description.sdp : '';

  return {
    present: true,
    type: typeof description.type === 'string' ? description.type : '',
    length: sdp.length,
    media: summarizeSdpMedia(sdp),
    candidates: summarizeSdpCandidates(sdp),
    sdp,
  };
}

/**
 * Sums the inbound VIDEO counters of a stats report.
 *
 * A report with no inbound video is `trackCount: 0` with zeroed counters — **not** a throw.
 * Audio-only calls and reports taken before the video transceiver exists are ordinary
 * observations, and the reader (not this function) decides whether they are a failure.
 */
export function summarizeInboundVideo(entries: readonly TRawStat[]): IInboundVideo {
  return entries.filter(isInboundVideo).reduce<IInboundVideo>(
    (total, entry) => ({
      framesDecoded: total.framesDecoded + num(entry, 'framesDecoded', 0),
      framesReceived: total.framesReceived + num(entry, 'framesReceived', 0),
      packetsReceived: total.packetsReceived + num(entry, 'packetsReceived', 0),
      packetsLost: total.packetsLost + num(entry, 'packetsLost', 0),
      jitterMs: total.jitterMs + Math.round(num(entry, 'jitter', 0) * 1000),
      trackCount: total.trackCount + 1,
    }),
    { framesDecoded: 0, framesReceived: 0, packetsReceived: 0, packetsLost: 0, jitterMs: 0, trackCount: 0 }
  );
}

/** Sums the outbound VIDEO counters of a stats report. Same zero-not-throw contract. */
export function summarizeOutboundVideo(entries: readonly TRawStat[]): IOutboundVideo {
  return entries.filter(isOutboundVideo).reduce<IOutboundVideo>(
    (total, entry) => ({
      framesSent: total.framesSent + num(entry, 'framesSent', 0),
      framesEncoded: total.framesEncoded + num(entry, 'framesEncoded', 0),
      packetsSent: total.packetsSent + num(entry, 'packetsSent', 0),
      trackCount: total.trackCount + 1,
      qualityLimitationReason: pickQualityLimitationReason(
        total.qualityLimitationReason,
        str(entry, 'qualityLimitationReason')
      ),
    }),
    { framesSent: 0, framesEncoded: 0, packetsSent: 0, trackCount: 0, qualityLimitationReason: '' }
  );
}

/**
 * Final review W1. Chrome reports `'none'` — not `''` — on an unconstrained `outbound-rtp` entry,
 * so `current || candidate` (the old reduce step) let the FIRST entry's `'none'` win outright and
 * mask every later entry's real reason: `'none'` is truthy, `||` never looks past it. With two
 * outbound video entries (simulcast layers, or a camera plus a second sender), a recording would
 * read "no quality problem" on exactly the bandwidth-limited peer an incident is about.
 *
 * The first value that is neither `''` nor `'none'` wins, in entry order — a genuine reason is
 * never displaced once found. Absent that, `'none'` wins over `''` if EITHER side actually reported
 * it: `''` on its own means "no outbound video entry" or "the browser does not implement the field"
 * (Firefox), which is strictly less information than an explicit "nothing limited this one".
 */
function pickQualityLimitationReason(current: string, candidate: string): string {
  const isReal = (reason: string): boolean => reason !== '' && reason !== 'none';

  if (isReal(current)) {
    return current;
  }

  if (isReal(candidate)) {
    return candidate;
  }

  return current === 'none' ? current : candidate;
}

/** Sums the inbound AUDIO counters of a stats report — the audio twin of {@link summarizeInboundVideo}. */
export function summarizeInboundAudio(entries: readonly TRawStat[]): IInboundAudio {
  return entries.filter(isInboundAudio).reduce<IInboundAudio>(
    (total, entry) => ({
      packetsReceived: total.packetsReceived + num(entry, 'packetsReceived', 0),
      packetsLost: total.packetsLost + num(entry, 'packetsLost', 0),
      bytesReceived: total.bytesReceived + num(entry, 'bytesReceived', 0),
      audioLevel: total.audioLevel + num(entry, 'audioLevel', 0),
      totalAudioEnergy: total.totalAudioEnergy + num(entry, 'totalAudioEnergy', 0),
      trackCount: total.trackCount + 1,
    }),
    { packetsReceived: 0, packetsLost: 0, bytesReceived: 0, audioLevel: 0, totalAudioEnergy: 0, trackCount: 0 }
  );
}

/**
 * Finds the selected candidate pair and resolves both of its candidates.
 *
 * Four fallbacks, because no two engines agree: Chromium publishes
 * `transport.selectedCandidatePairId`, Firefox sets `selected: true` on the pair itself, and older
 * shapes only ever mark one pair `nominated` + `succeeded`. A report with no succeeded pair yields
 * empty strings, which read as "not connected yet" rather than as a missing measurement.
 */
export function selectSelectedPair(entries: readonly TRawStat[]): ISelectedPair {
  const byId = new Map<string, TRawStat>();

  for (const entry of entries) {
    const id = str(entry, 'id');

    if (id) {
      byId.set(id, entry);
    }
  }

  const pair = findSelectedPair(entries, byId);

  if (!pair) {
    return emptyPair();
  }

  const local = byId.get(str(pair, 'localCandidateId'));
  const remote = byId.get(str(pair, 'remoteCandidateId'));
  const localType = local ? str(local, 'candidateType') : '';
  const remoteType = remote ? str(remote, 'candidateType') : '';

  return {
    localType,
    remoteType,
    protocol: local ? str(local, 'protocol') : '',
    relayProtocol: local ? str(local, 'relayProtocol') : '',
    // Raw, and beside the `remoteType`/`localType` the redactor tiers each one against: a relay
    // address is our own TURN server's and is kept verbatim, every other type is a peer's.
    remoteAddress: remote ? str(remote, 'address') : '',
    localAddress: local ? str(local, 'address') : '',
    url: local ? str(local, 'url') : '',
    roundTripMs: Math.round(num(pair, 'currentRoundTripTime', -0.001) * 1000),
    availableOutgoingBitrate: num(pair, 'availableOutgoingBitrate', -1),
  };
}

/** Projects one peer's whole report into the four summaries the recorder keeps. */
export function projectPeerStats(entries: readonly TRawStat[]): IPeerStats {
  return {
    inbound: summarizeInboundVideo(entries),
    outbound: summarizeOutboundVideo(entries),
    audio: summarizeInboundAudio(entries),
    pair: selectSelectedPair(entries),
  };
}

/** Turns a live `RTCStatsReport` into the plain records the projection functions read. */
export function statsEntries(report: RTCStatsReport | undefined): TRawStat[] {
  if (!report) {
    return [];
  }

  const collected: TRawStat[] = [];

  // `RTCStatsReport` is a ReadonlyMap whose values are typed `any` by the DOM lib; going through
  // `unknown` is what keeps that `any` from spreading into this module.
  for (const value of Array.from(report.values()) as unknown[]) {
    if (typeof value === 'object' && value !== null) {
      collected.push(value as TRawStat);
    }
  }

  return collected;
}

/**
 * The `getStats()` sampler.
 *
 * Parked until {@link IRtcStatsSampler.arm} is called — there is nothing to sample before a peer
 * connection exists, so the tap arms it from the peer-connection seam rather than at install. That
 * is also what carries it across a takeover: `RTCClient.destroy()` nulls the singleton and the
 * rebuilt client constructs fresh peer connections, each of which re-arms a parked loop, while
 * `readPeers` re-resolves the live client on every tick regardless.
 *
 * The tick is `setTimeout`-rearmed rather than `setInterval`: the cadence changes with the adaptive
 * throttle, and a `getStats()` pass that outruns its own interval must never stack.
 */
export function createRtcStatsSampler(options: IRtcStatsSamplerOptions): IRtcStatsSampler {
  const history = new Map<string, IPeerHistory>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let idleTicks = 0;
  let running = false;

  function schedule(): void {
    if (timer !== null) {
      return;
    }

    timer = setTimeout(
      () => {
        timer = null;
        void tick();
      },
      rtcStatsIntervalMs(options.baseIntervalMs(), options.samplingMultiplier())
    );
  }

  async function tick(): Promise<void> {
    if (!running) {
      return;
    }

    // The budget gate. Nothing above this line touches a peer connection, so a recorder that is
    // off, killed, breaker-tripped or shedding this lane costs one boolean read per interval.
    if (!options.shouldSample()) {
      if (options.isTerminated()) {
        stop();
        return;
      }

      schedule();
      return;
    }

    const peers = options.readPeers();

    if (peers.length === 0) {
      idleTicks += 1;

      if (idleTicks >= RTC_STATS_IDLE_TICKS_BEFORE_PARK) {
        stop();
        return;
      }

      schedule();
      return;
    }

    idleTicks = 0;

    const sample = await collect(peers);

    // `stop()` can land while the per-peer reads are in flight (an uninstall, a kill switch, a
    // parked loop). Without this the sample would be emitted after teardown AND `schedule()` below
    // would arm a timer nothing owns any more.
    if (!running) {
      return;
    }

    options.onSample(sample);

    const changed = diffHealth(peers, sample, history);

    if (changed.length > 0) {
      options.onHealth(changed);
    }

    schedule();
  }

  function stop(): void {
    running = false;
    idleTicks = 0;
    history.clear();

    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  return {
    arm(): void {
      if (running) {
        return;
      }

      running = true;
      idleTicks = 0;
      schedule();
    },
    stop,
    isArmed(): boolean {
      return running;
    },
  };
}

/** One pass over every peer, building the columnar sample. */
async function collect(peers: readonly IRtcStatsPeer[]): Promise<IRtcStatsSample> {
  const sample = emptySample();

  for (const peer of peers) {
    const report = await readStats(peer.peerConnection);
    const stats = projectPeerStats(report.entries);

    if (report.failed) {
      sample.failed.push(peer.connectionId);
    }

    sample.peers.push(peer.connectionId);
    sample.framesDecoded.push(stats.inbound.framesDecoded);
    sample.framesReceived.push(stats.inbound.framesReceived);
    sample.framesSent.push(stats.outbound.framesSent);
    sample.framesEncoded.push(stats.outbound.framesEncoded);
    sample.packetsReceived.push(stats.inbound.packetsReceived);
    sample.packetsLost.push(stats.inbound.packetsLost);
    sample.packetsSent.push(stats.outbound.packetsSent);
    sample.jitterMs.push(stats.inbound.jitterMs);
    sample.roundTripMs.push(stats.pair.roundTripMs);
    sample.localCandidateType.push(stats.pair.localType);
    sample.remoteCandidateType.push(stats.pair.remoteType);
    sample.candidateProtocol.push(stats.pair.protocol);
    sample.relayProtocol.push(stats.pair.relayProtocol);
    sample.remoteCandidateAddress.push(stats.pair.remoteAddress);
    sample.turnUrl.push(stats.pair.url);
    sample.availableOutgoingBitrate.push(stats.pair.availableOutgoingBitrate);
    sample.inboundVideoTracks.push(stats.inbound.trackCount);
    sample.outboundVideoTracks.push(stats.outbound.trackCount);
    sample.hasStream.push(peer.hasStream);
    sample.transportFailed.push(peer.transportFailed);
    sample.connectionState.push(peerState(peer.peerConnection, 'connectionState'));
    sample.iceConnectionState.push(peerState(peer.peerConnection, 'iceConnectionState'));
    sample.audioPacketsReceived.push(stats.audio.packetsReceived);
    sample.audioPacketsLost.push(stats.audio.packetsLost);
    sample.audioBytesReceived.push(stats.audio.bytesReceived);
    sample.audioLevel.push(stats.audio.audioLevel);
    sample.totalAudioEnergy.push(stats.audio.totalAudioEnergy);
    sample.audioTracks.push(stats.audio.trackCount);
    sample.videoQualityLimitationReason.push(stats.outbound.qualityLimitationReason);
  }

  return sample;
}

/**
 * One `getStats()` read that reports its own failure instead of throwing.
 *
 * A read that rejects (the pc closed under us mid-tick) is not the same observation as a peer that
 * is silent, and conflating the two is how a recording invents an outage.
 */
async function readStats(peerConnection: RTCPeerConnection): Promise<{ entries: TRawStat[]; failed: boolean }> {
  let report: RTCStatsReport;

  try {
    report = await peerConnection.getStats();
  } catch {
    return { entries: [], failed: true };
  }

  return { entries: statsEntries(report), failed: false };
}

/**
 * The media-health verdict, emitted only for peers whose reading CHANGED.
 *
 * Change-driven rather than per-tick: a steady 7-party call would otherwise write seven entries
 * every three seconds for the whole session, and none of them would say anything.
 */
function diffHealth(
  peers: readonly IRtcStatsPeer[],
  sample: IRtcStatsSample,
  history: Map<string, IPeerHistory>
): IMediaHealthEntry[] {
  const changed: IMediaHealthEntry[] = [];
  const seen = new Set<string>();

  peers.forEach((peer, index) => {
    seen.add(peer.connectionId);

    const framesDecoded = sample.framesDecoded[index];
    const connectionState = sample.connectionState[index];
    const previous = history.get(peer.connectionId);
    const delta = previous ? framesDecoded - previous.framesDecoded : framesDecoded;
    const decoding = delta > 0;
    const everDecoded = (previous?.everDecoded ?? false) || framesDecoded > 0;
    const reasons = changeReasons(previous, {
      framesDecoded,
      everDecoded,
      hasStream: peer.hasStream,
      transportFailed: peer.transportFailed,
      decoding,
      connectionState,
    });

    history.set(peer.connectionId, {
      framesDecoded,
      everDecoded,
      hasStream: peer.hasStream,
      transportFailed: peer.transportFailed,
      decoding,
      connectionState,
    });

    if (reasons.length === 0) {
      return;
    }

    changed.push({
      connectionId: peer.connectionId,
      hasStream: peer.hasStream,
      transportFailed: peer.transportFailed,
      framesDecoded,
      framesDecodedDelta: delta,
      decoding,
      everDecoded,
      connectionState,
      iceConnectionState: sample.iceConnectionState[index],
      changed: reasons,
    });
  });

  for (const key of [...history.keys()]) {
    if (!seen.has(key)) {
      history.delete(key);
    }
  }

  return changed;
}

/** Which fields moved since the previous tick. A first sighting reports `first`. */
function changeReasons(previous: IPeerHistory | undefined, current: IPeerHistory): string[] {
  if (!previous) {
    return ['first'];
  }

  const reasons: string[] = [];

  if (previous.decoding !== current.decoding) {
    reasons.push('decoding');
  }

  if (previous.hasStream !== current.hasStream) {
    reasons.push('stream');
  }

  if (previous.transportFailed !== current.transportFailed) {
    reasons.push('transportFailed');
  }

  if (previous.connectionState !== current.connectionState) {
    reasons.push('connectionState');
  }

  return reasons;
}

function emptySample(): IRtcStatsSample {
  return {
    peers: [],
    framesDecoded: [],
    framesReceived: [],
    framesSent: [],
    framesEncoded: [],
    packetsReceived: [],
    packetsLost: [],
    packetsSent: [],
    jitterMs: [],
    roundTripMs: [],
    localCandidateType: [],
    remoteCandidateType: [],
    candidateProtocol: [],
    relayProtocol: [],
    remoteCandidateAddress: [],
    turnUrl: [],
    availableOutgoingBitrate: [],
    inboundVideoTracks: [],
    outboundVideoTracks: [],
    hasStream: [],
    transportFailed: [],
    connectionState: [],
    iceConnectionState: [],
    failed: [],
    audioPacketsReceived: [],
    audioPacketsLost: [],
    audioBytesReceived: [],
    audioLevel: [],
    totalAudioEnergy: [],
    audioTracks: [],
    videoQualityLimitationReason: [],
  };
}

function emptyPair(): ISelectedPair {
  return {
    localType: '',
    remoteType: '',
    protocol: '',
    relayProtocol: '',
    remoteAddress: '',
    localAddress: '',
    url: '',
    roundTripMs: -1,
    availableOutgoingBitrate: -1,
  };
}

function findSelectedPair(entries: readonly TRawStat[], byId: Map<string, TRawStat>): TRawStat | undefined {
  const pairs = entries.filter((entry) => str(entry, 'type') === 'candidate-pair');
  const transport = entries.find((entry) => str(entry, 'type') === 'transport');
  const selectedId = transport ? str(transport, 'selectedCandidatePairId') : '';
  const byTransport = selectedId ? byId.get(selectedId) : undefined;

  return (
    byTransport ??
    pairs.find((pair) => pair.selected === true) ??
    pairs.find((pair) => pair.nominated === true && str(pair, 'state') === 'succeeded') ??
    pairs.find((pair) => str(pair, 'state') === 'succeeded')
  );
}

function isInboundVideo(entry: TRawStat): boolean {
  return str(entry, 'type') === 'inbound-rtp' && isVideo(entry);
}

function isOutboundVideo(entry: TRawStat): boolean {
  return str(entry, 'type') === 'outbound-rtp' && isVideo(entry);
}

function isInboundAudio(entry: TRawStat): boolean {
  return str(entry, 'type') === 'inbound-rtp' && isAudio(entry);
}

/** Tolerates both the modern `kind` and the legacy `mediaType` field. */
function isVideo(entry: TRawStat): boolean {
  return (str(entry, 'kind') || str(entry, 'mediaType')) === 'video';
}

/** Tolerates both the modern `kind` and the legacy `mediaType` field. */
function isAudio(entry: TRawStat): boolean {
  return (str(entry, 'kind') || str(entry, 'mediaType')) === 'audio';
}

/** Reads a peer-connection state without assuming the property exists (a closed/mocked pc). */
function peerState(peerConnection: RTCPeerConnection, key: 'connectionState' | 'iceConnectionState'): string {
  const value = (peerConnection as unknown as TRawStat)[key];

  return typeof value === 'string' ? value : '';
}

/** `m=<kind> ...` plus the direction attribute that follows it, per media section. */
function summarizeSdpMedia(sdp: string): Array<{ kind: string; direction: string; mid: string }> {
  const media: Array<{ kind: string; direction: string; mid: string }> = [];
  const directions = new Set(['sendrecv', 'sendonly', 'recvonly', 'inactive']);

  for (const raw of sdp.split('\n')) {
    const line = raw.trim();

    if (line.startsWith('m=')) {
      media.push({ kind: line.slice(2).split(' ')[0] ?? '', direction: '', mid: '' });
      continue;
    }

    const current = media[media.length - 1];

    if (!current) {
      continue;
    }

    if (line.startsWith('a=mid:')) {
      current.mid = line.slice('a=mid:'.length);
      continue;
    }

    const attribute = line.startsWith('a=') ? line.slice(2) : '';

    if (directions.has(attribute)) {
      current.direction = attribute;
    }
  }

  return media;
}

interface IParsedCandidate {
  type: string;
  protocol: string;
  address: string;
  port: number;
  relatedAddress: string;
}

/** Parses the `candidate:` line both `RTCIceCandidate.candidate` and `a=candidate:` carry. */
function parseCandidateLine(line: string): IParsedCandidate {
  const empty: IParsedCandidate = { type: '', protocol: '', address: '', port: -1, relatedAddress: '' };

  if (!line) {
    return empty;
  }

  const tokens = line.replace(/^a=/, '').split(' ');

  if (tokens.length < 8 || !tokens[0].startsWith('candidate:')) {
    return empty;
  }

  const typIndex = tokens.indexOf('typ');
  const raddrIndex = tokens.indexOf('raddr');
  const port = Number(tokens[5]);

  return {
    type: typIndex >= 0 ? (tokens[typIndex + 1] ?? '') : '',
    protocol: tokens[2]?.toLowerCase() ?? '',
    address: tokens[4] ?? '',
    port: Number.isFinite(port) ? port : -1,
    relatedAddress: raddrIndex >= 0 ? (tokens[raddrIndex + 1] ?? '') : '',
  };
}

function num(source: TRawStat, key: string, fallback: number): number {
  const value = source[key];

  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function str(source: TRawStat, key: string): string {
  const value = source[key];

  return typeof value === 'string' ? value : '';
}
